import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../components/Footer';
import { BlogCTAPopup } from '../components/BlogCTAPopup';
import { ArrowLeft, Calendar, Clock, Share2, Calculator, Phone, MapPin } from 'lucide-react';
import { getBlogPost, getRecentBlogPosts, BlogPost } from '../lib/supabase';
import { motion } from 'framer-motion';

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    async function fetchPost() {
      if (!slug) return;
      
      try {
        const [blogPost, recent] = await Promise.all([
          getBlogPost(slug),
          getRecentBlogPosts(3)
        ]);
        
        if (blogPost) {
          setPost(blogPost);
          setRecentPosts(recent.filter(p => p.id !== blogPost.id));
        } else {
          setNotFound(true);
        }
      } catch (error) {
        // Silently handle the error - don't show console errors for missing table
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
  }, [slug]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readingTime} min read`;
  };

  const sharePost = () => {
    if (navigator.share && post) {
      navigator.share({
        title: post.title,
        text: post.description,
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400 mx-auto mb-4"></div>
          <p className="text-zinc-400">Loading blog post...</p>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-zinc-900">
        <main className="pt-24">
          <div className="max-w-4xl mx-auto px-6 py-16 text-center">
            <h1 className="text-4xl font-light text-zinc-100 mb-4">Post Not Found</h1>
            <p className="text-zinc-400 mb-8">The blog post you're looking for doesn't exist or has been removed.</p>
            <Link 
              to="/blog"
              className="inline-flex items-center gap-2 px-6 py-3 bg-yellow-400 text-zinc-900 rounded-lg hover:bg-yellow-500 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Blog
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>{post.meta_title || post.title} | Golden Touch Contracting</title>
        <meta name="description" content={post.meta_description || post.description} />
        {post.keywords && (
          <meta name="keywords" content={Array.isArray(post.keywords) ? post.keywords.join(', ') : post.keywords} />
        )}
        <link rel="canonical" href={`https://renovationwestchester.com/blog/${post.slug}`} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://renovationwestchester.com/blog/${post.slug}`} />
        {post.image_url && <meta property="og:image" content={post.image_url} />}

        {/* Article specific tags */}
        <meta property="article:published_time" content={post.published_at || post.created_at} />
        <meta property="article:modified_time" content={post.updated_at} />
      </Helmet>

      <BlogCTAPopup />

      <main className="pt-24">
        <article className="max-w-4xl mx-auto px-6 py-16">
          <Link to="/blog" className="inline-flex items-center gap-2 text-yellow-400 mb-8 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Blog</span>
          </Link>

          <motion.header 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="mb-12"
          >
            {post.image_url && (
              <div className="aspect-[16/9] overflow-hidden rounded-xl mb-8">
                <img 
                  src={post.image_url} 
                  alt={post.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-6 text-sm text-zinc-400">
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(post.published_at || post.created_at)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  <span>{getReadingTime(post.content)}</span>
                </div>
              </div>
              
              <button
                onClick={sharePost}
                className="flex items-center gap-2 text-zinc-400 hover:text-yellow-400 transition-colors"
              >
                <Share2 className="h-4 w-4" />
                <span className="text-sm">Share</span>
              </button>
            </div>

            <h1 className="text-3xl md:text-5xl font-light text-zinc-100 mb-4">
              {post.title}
            </h1>
            
            <p className="text-lg text-zinc-400 leading-relaxed">
              {post.description}
            </p>
          </motion.header>

          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="prose prose-invert prose-lg max-w-none mb-16"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {recentPosts.length > 0 && (
            <motion.section 
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="border-t border-zinc-800 pt-12"
            >
              <h2 className="text-2xl font-light text-zinc-100 mb-8">Recent Posts</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recentPosts.map((recentPost) => (
                  <Link
                    key={recentPost.id}
                    to={`/blog/${recentPost.slug}`}
                    className="bg-zinc-800/30 rounded-lg p-6 hover:bg-zinc-700/30 transition-colors group"
                  >
                    <h3 className="text-lg text-zinc-100 mb-2 group-hover:text-yellow-400 transition-colors">
                      {recentPost.title}
                    </h3>
                    <p className="text-zinc-400 text-sm line-clamp-2 mb-3">
                      {recentPost.description}
                    </p>
                    <div className="text-xs text-zinc-500">
                      {formatDate(recentPost.published_at || recentPost.created_at)}
                    </div>
                  </Link>
                ))}
              </div>
            </motion.section>
          )}
        </article>

        {/* CTA Section */}
        <div className="max-w-4xl mx-auto px-6 mb-16">
          <div className="bg-gradient-to-r from-zinc-800/50 via-zinc-800/30 to-zinc-800/50 rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/5 via-transparent to-yellow-400/5 opacity-50"></div>
            <div className="absolute top-4 right-4 w-32 h-32 bg-yellow-400/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-4 left-4 w-24 h-24 bg-yellow-400/10 rounded-full blur-2xl"></div>
            
            <div className="relative z-10">
              <h2 className="text-2xl sm:text-3xl font-light text-zinc-100 mb-4">
                Inspired by This Project?
              </h2>
              <p className="text-zinc-400 mb-8 max-w-2xl mx-auto leading-relaxed">
                Get an instant AI-powered estimate for your renovation project in under 1 minute, 
                or speak directly with our experts to bring your vision to life.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6">
                <button
                  onClick={() => {
                    const aiButton = document.querySelector('.fixed.bottom-4') as HTMLButtonElement;
                    if (aiButton) aiButton.click();
                  }}
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-zinc-900 rounded-xl hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-white/20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                  <Calculator className="h-5 w-5 relative z-10" />
                  <div className="relative z-10">
                    <div className="text-sm font-medium">Get Online Estimate</div>
                    <div className="text-xs opacity-80">Takes less than 1 minute</div>
                  </div>
                </button>
                
                <Link
                  to="/consultation"
                  className="group inline-flex items-center gap-3 px-8 py-4 bg-zinc-800/50 backdrop-blur-sm border border-yellow-400/30 text-yellow-400 rounded-xl hover:bg-yellow-400 hover:text-zinc-900 transition-all duration-300 relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-yellow-400 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                  <Phone className="h-5 w-5 relative z-10" />
                  <div className="relative z-10">
                    <div className="text-sm font-medium">Free Consultation</div>
                    <div className="text-xs opacity-80">Speak with an expert</div>
                  </div>
                </Link>
              </div>
              
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-zinc-500">
                <span>Or call us directly:</span>
                <a 
                  href="tel:9149606433" 
                  className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium"
                >
                  (914) 960-6433
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Cities/Towns SEO Section */}
        <motion.section 
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-4xl mx-auto px-6 mb-16"
        >
          <div className="bg-gradient-to-br from-zinc-800/30 to-zinc-800/10 rounded-2xl p-8 border border-zinc-700/50">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-light text-zinc-100 mb-4">
                Serving Westchester County, NY & Fairfield County, CT
              </h2>
              <p className="text-zinc-400 max-w-2xl mx-auto leading-relaxed">
                Golden Touch Contracting provides premium renovation and construction services 
                throughout Westchester County, New York and Fairfield County, Connecticut. 
                Licensed and insured in both states.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-lg text-yellow-400 mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Westchester County, NY
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    'Scarsdale', 'Rye', 'Harrison', 'Mamaroneck', 'Larchmont',
                    'New Rochelle', 'White Plains', 'Bronxville', 'Eastchester',
                    'Tuckahoe', 'Yonkers', 'Mount Vernon', 'Pelham', 'Pelham Manor',
                    'Dobbs Ferry', 'Irvington', 'Tarrytown', 'Sleepy Hollow',
                    'Ardsley', 'Hastings-on-Hudson', 'Edgemont', 'Hartsdale'
                  ].map((city, index) => (
                    <div key={index} className="text-zinc-300 text-sm py-1 px-2 bg-zinc-700/30 rounded">
                      {city}
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h3 className="text-lg text-yellow-400 mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  Fairfield County, CT
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    'Greenwich', 'Stamford', 'Norwalk', 'Westport', 'Darien',
                    'New Canaan', 'Wilton', 'Ridgefield', 'Fairfield', 'Trumbull',
                    'Easton', 'Weston', 'Redding', 'Bethel', 'Danbury',
                    'Brookfield', 'New Fairfield', 'Sherman', 'Monroe', 'Shelton'
                  ].map((city, index) => (
                    <div key={index} className="text-zinc-300 text-sm py-1 px-2 bg-zinc-700/30 rounded">
                      {city}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="mt-8 pt-6 border-t border-zinc-700/50 text-center">
              <p className="text-zinc-400 text-sm mb-4">
                Professional renovation services including kitchen remodeling, bathroom renovation, 
                home additions, basement finishing, and custom construction projects.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a 
                  href="tel:9149606433" 
                  className="text-yellow-400 hover:text-yellow-300 transition-colors font-medium"
                >
                  Call (914) 960-6433
                </a>
                <span className="hidden sm:block text-zinc-600">•</span>
                <Link 
                  to="/consultation"
                  className="text-yellow-400 hover:text-yellow-300 transition-colors"
                >
                  Free Consultation
                </Link>
              </div>
            </div>
          </div>
        </motion.section>
      </main>

      <Footer />
    </div>
  );
}